import React from 'react';
import moment from 'moment';
import _ from 'lodash';
import { formatTravelItineraryList } from 'selectors/travel.selectors';
import { Bookings } from 'views/travel/Bookings';
import { THEME_NAMES } from 'selectors/tag.selectors';
import { AuditHistory } from 'components/Audit';
import { DocLink, DocPages } from 'components/Help/';
import { CanViewTravel } from 'library/permissions';
import ItineraryFormInput from './input';

const config = {
  init: {
    active: true
  },
  // List Table
  list: {
    // Table Columns
    format: formatTravelItineraryList,
    columns: [
      {
        title: 'Traveller',
        dataIndex: 'user__last_name',
        width: '220px',
        sorter: true,
        render: (text, record) => `${record.user.last_name.toUpperCase()}/${record.user.first_name.toUpperCase()}`
      },
      {
        title: 'Booking Summary',
        dataIndex: 'id',
        sorter: false,
        render: (text, record) => {
          const { bookings } = record;
          if (!bookings.length) {
            return (
              <div className="bhp-booking-summary">
                No bookings available
              </div>
            );
          }

          // get departure info
          const { 0: from } = bookings.sort((current, next) => current.departure_date - next.departure_date);
          const { departure_date } = from;
          const { departure_location } = from.leg.route;
          const departureLocation = `${departure_location.name} (${departure_location.code})`;
          // get arrival info
          const { length: l, [l - 1]: to } = bookings.sort((current, next) => current.arrival_date - next.arrival_date);
          const { arrival_date } = to;
          const { arrival_location } = to.leg.route;
          const arrivalLocation = `${arrival_location.name} (${arrival_location.code})`;
          const stops = record.bookings.length ? record.bookings.length - 1 : 0;

          return (
            <div className="bhp-booking-summary">
              {!_.isEmpty(from) && (
                <div>
                  <span>Departing: </span>
                  {`${departureLocation} on ${moment(departure_date)
                    .format('DD/MM/YYYY HH:mm')}`}
                </div>
              )}
              {!_.isEmpty(to) && (
                <div>
                  <span>Arriving: </span>
                  {`${arrivalLocation} on ${moment(arrival_date)
                    .format('DD/MM/YYYY HH:mm')}`}
                </div>
              )}
              {stops > 0 && `${stops} Stops`}
            </div>
          );
        }
      },
      {
        title: 'Travel Type',
        dataIndex: 'travel_type',
        width: '180px',
        sorter: true,
        filterMultiple: false,
        contains: false,
        filters: [{
          text: 'In',
          value: 'In'
        }, {
          text: 'Out',
          value: 'Out'
        }, {
          text: 'Intra',
          value: 'Intra'
        }]
      },
      {
        title: 'Status',
        dataIndex: 'booking_status',
        width: '100px',
        align: 'center',
        sorter: false
      },
      {
        title: 'Itinerary Reference',
        dataIndex: 'reference_number',
        width: '180px',
        sorter: true,
        defaultSortOrder: 'ascend'
      }
    ],
    // Deleting
    confirmMessage: item => (
      <div>
        <p>
          Are you sure you wish to delete the itinerary
          <b>{item.reference_number}</b>
          ?
        </p>
        <p>
          This action cannot be undone.
        </p>
      </div>
    ),
    // Filtering
    sorter: {
      field: 'name',
      order: 'ascend'
    },
    filters: {},
    customFilters: {
      reference_number: {
        filterDropdownVisible: false,
        filtered: false,
        contains: true,
        searchFields: ['reference_number'],
        searchValue: ''
      },
      user__last_name: {
        filterDropdownVisible: false,
        filtered: false,
        contains: true,
        searchFields: ['user__last_name'],
        searchValue: ''
      }
    },
    // General
    title: <DocLink page={DocPages.travelItineraries}>Itineraries</DocLink>
  },
  // Detail Screen
  detail: {
    // General
    emptyMessage: 'Please select an Itinerary',
    title: 'Itinerary',
    tabItems: [
      {
        key: 0,
        title: 'Details',
        component: props => (
          <ItineraryFormInput
            {...props}
            title="Itinerary"
          />
        )
      },
      {
        key: 1,
        title: 'Bookings',
        component: props => (
          <Bookings
            {...props}
          />
        )
      },
      {
        key: 2,
        title: 'History',
        component: props => (
          <AuditHistory
            {...props}
            themeName={THEME_NAMES.itinerary}
          />
        ),
        show: () => CanViewTravel()
          .hasPermissions()
      }
    ]
  }
};

export default config;
